// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createActionsHook,
	createStore,
	createContainer,
	type StoreActionApi,
	type Action,
	type BoundActions,
	type HookActionsFunction,
	type OverrideContainerComponent,
	type Store,
} from '@atlassian/react-sweet-state';
import { fg } from '@atlassian/jira-feature-gating';
import { addCollection } from './addCollection.tsx';
import { loadCollections, type LoadCollectionsOptions } from './loadCollections.tsx';
import { deleteCollection } from './deleteCollection.tsx';
import { copyCollection } from './copyCollection.tsx';
import { expandSectionForSelectedCollection } from './expandSectionForSelectedCollection.tsx';
import { updateCollection } from './updateCollection.tsx';
import type {
	State,
	Props,
	NavigationCollection,
	DeleteCallback,
} from './NavigationCollection.types.tsx';

type Actions = {
	addCollection: (collection: NavigationCollection) => Action<State, Props>;
	loadCollections: (
		options?: LoadCollectionsOptions,
	) => Action<State, Props, Promise<NavigationCollection[]>>;
	expandSectionForSelectedCollection: () => Action<State, Props>;
	updateCollection: (
		collectionUUID: string,
		patch: Partial<NavigationCollection>,
	) => Action<State, Props>;
	deleteCollection: (collectionUUID: string) => Action<State, Props>;
	copyCollection: (
		collectionUUID: string,
		patch: {
			uuid: string | null;
			name: string;
			ownerId: string | null;
		},
	) => Action<State, Props>;
	addDeleteListener: (callback: DeleteCallback) => Action<State, Props>;
	removeDeleteListener: (callback: DeleteCallback) => Action<State, Props>;
	runDeleteListeners: () => Action<State, Props>;
};

const addDeleteListener =
	(callback: DeleteCallback): Action<State, Props> =>
	({ getState, setState }) => {
		if (!getState().deleteCallbacks.includes(callback) && fg('jpd-enable-roadmaps-recents-fe')) {
			setState({ deleteCallbacks: [...getState().deleteCallbacks, callback] });
		}
	};

const removeDeleteListener =
	(callback: DeleteCallback): Action<State, Props> =>
	({ getState, setState }) => {
		if (fg('jpd-enable-roadmaps-recents-fe')) {
			setState({
				deleteCallbacks: getState().deleteCallbacks.filter((cb) => cb !== callback),
			});
		}
	};

const runDeleteListeners =
	(): Action<State, Props> =>
	({ getState }) => {
		if (fg('jpd-enable-roadmaps-recents-fe')) {
			getState().deleteCallbacks.forEach((cb) => cb());
		}
	};

const actions: Actions = {
	addCollection,
	loadCollections,
	expandSectionForSelectedCollection,
	updateCollection,
	deleteCollection,
	copyCollection,
	addDeleteListener,
	removeDeleteListener,
	runDeleteListeners,
};

const initialState: State = {
	collections: {
		data: [],
		error: null,
		initialized: false,
		isLoading: false,
	},
	activeCollectionUUID: undefined,
	deleteCallbacks: [],
};

export const NavigationCollectionsStore: Store<State, Actions, Props> = createStore<
	State,
	Actions,
	Props
>({
	initialState,
	actions,
	name: 'PolarisNavigationCollectionsStore',
});

const onPropsChange = ({ setState }: StoreActionApi<State>, props: Props) => {
	setState({
		activeCollectionUUID: props.activeCollectionUUID,
	});
};

export const NavigationCollectionsContainer: OverrideContainerComponent<Props> = createContainer<
	State,
	Actions,
	Props
>(NavigationCollectionsStore, {
	onInit: () => onPropsChange,
	onUpdate: () => onPropsChange,
});

export const useNavigationCollectionsActions: HookActionsFunction<BoundActions<State, Actions>> =
	createActionsHook(NavigationCollectionsStore);
